import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiArrowRight } = FiIcons;

const units = [
  {
    id: "310",
    title: 'Nature Green',
    floor: '3rd fl.',
    bgColor: 'bg-[#064e3b]',
    videoUrl: 'https://res.cloudinary.com/dwumujtpu/video/upload/q_auto/v1777158443/1-7601_Canada_Ave_310_uzqdfg.mp4',
  },
  {
    id: "414",
    title: 'Royal Blue',
    floor: '4th fl.',
    bgColor: 'bg-[#0047ab]',
    videoUrl: 'https://res.cloudinary.com/dwumujtpu/video/upload/q_auto/v1777169468/4f961f75-f8f6-4ae5-9d33-65900c00ba9d_epge8o.mov',
  },
  {
    id: "612",
    title: 'Cozy Grey',
    floor: '6th fl.',
    bgColor: 'bg-[#36454f]',
    videoUrl: 'https://res.cloudinary.com/dwumujtpu/video/upload/q_auto/v1777156638/23-7601_Canada_Ave_612_fqxsrw.mp4',
  }
];

const Hero = () => {
  return (
    <section className="relative min-h-screen bg-[#faf8f5] pt-32 pb-24 md:pt-40 md:pb-32 flex flex-col items-center justify-center overflow-hidden">
      <div className="absolute inset-0 z-0">
        <img
          src="https://res.cloudinary.com/dwumujtpu/image/upload/q_auto/v1777158443/1-7601_Canada_Ave_310_uzqdfg.jpg"
          alt="Nature Green suite at 7601 Canada Avenue in Orlando"
          className="w-full h-full object-cover opacity-20"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-[#faf8f5]/70 via-[#faf8f5]/90 to-[#faf8f5]"></div>
      </div>
      
      <div className="relative z-10 w-full max-w-7xl mx-auto px-6 lg:px-8">
        
        {/* Hero Top Section - Stacked on mobile, side-by-side on tablet/desktop */}
        <div className="flex flex-col sm:flex-row items-center sm:items-start gap-8 sm:gap-12 lg:gap-20 mb-16 md:mb-24">
          
          {/* Square Image with Border Radius (Full width on mobile, 50% on sm+) */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8 }}
            className="w-full sm:w-1/2"
          >
            <div className="aspect-square w-full rounded-[32px] sm:rounded-[60px] overflow-hidden shadow-lg relative shrink-0">
              <img 
                src="https://media-manager-c.questera.ai/greta-media/a9dafbdca7a53505ee8e6c159db427394419e27bea345e5b19b45c8548ba1d55be4babe7870412394b37ceb3edc9196c/images/aW1hZ2Uvd2VicA==/ec12871d12588b52abe96b6c0a9c7a3c.webp" 
                alt="Exterior of 7601 Canada Avenue Orlando suites"
                className="w-full h-full object-cover"
              />
            </div>
          </motion.div>

          <div className="w-full sm:w-1/2 text-left pt-2 sm:pt-8">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              className="mb-2 sm:mb-4"
            >
               <span className="text-accent-orange font-black tracking-[0.1em] sm:tracking-[0.3em] uppercase text-[10px] underline underline-offset-8 decoration-2">Our Premium Suites</span>
            </motion.div>
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="text-6xl sm:text-7xl md:text-8xl lg:text-9xl font-serif font-black text-black uppercase tracking-tighter leading-[0.85] mb-4 sm:mb-8"
            >
              Orlando<br/>Suites
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
              className="hidden sm:block text-black/80 font-medium text-lg md:text-xl max-w-2xl leading-relaxed"
            >Your centrally located Orlando destination near International Drive, Universal Orlando, the Orange County Convention Center, and world-class dining, shopping, and entertainment.</motion.p>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 1, delay: 0.6 }}
              className="mt-4 sm:mt-8 flex flex-wrap gap-3 sm:gap-4"
            >
              <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-black/5 rounded-full border border-black/5 text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-black/40">
                Managed
              </div>
              <div className="px-3 sm:px-4 py-1.5 sm:py-2 bg-black/5 rounded-full border border-black/5 text-[8px] sm:text-[10px] font-black uppercase tracking-widest text-black/40">
                No fees
              </div>
            </motion.div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {units.map((item, index) => (
            <motion.div
              key={item.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: index * 0.1 }}
              className="group"
            >
              <Link to={`/${item.id}`}>
                <div className={`relative aspect-[4/5] ${item.bgColor} overflow-hidden rounded-[32px] cursor-pointer transition-all duration-700 hover:scale-[1.02] hover:shadow-2xl ring-1 ring-white/10`}>
                  <video
                    src={item.videoUrl}
                    className="absolute inset-0 w-full h-full object-cover opacity-60 group-hover:opacity-90 transition-opacity duration-700"
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/20 to-black/10 opacity-80 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="absolute top-6 right-6">
                    <div className="bg-black/40 backdrop-blur-md text-white px-4 py-1.5 rounded-full text-[10px] font-black uppercase tracking-[0.2em] border border-white/10 shadow-lg">
                      {item.floor}
                    </div>
                  </div>
                  
                  <div className="absolute bottom-8 left-8 right-8">
                    <h3 className="text-3xl lg:text-4xl font-serif font-black text-white uppercase tracking-tighter mb-3 transform group-hover:-translate-y-2 transition-transform duration-500">{item.title}</h3>
                    <div className="flex items-center gap-2 text-white/70 text-[10px] font-black uppercase tracking-widest group-hover:text-white transition-colors">
                      View Unit Details <SafeIcon icon={FiArrowRight} className="w-3 h-3 group-hover:translate-x-2 transition-transform duration-500" />
                    </div>
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Hero;